// OpenAI (GPT) helper for generating location fields.
//
// The OpenAI API key is NOT in this bundle. All requests go through our own
// backend proxy (see server.js), which holds the key server-side and verifies
// the caller's Firebase ID token before forwarding to OpenAI. In development
// run `node server.js` alongside `npm start` (CRA proxies /api to it).
import { auth } from './firebase';

// Availability of the proxy (and its key), checked once at module load.
// Optimistically true until the health check says otherwise, so the UI is
// never blocked just because the check hasn't finished yet.
// NOTE: a misconfigured host returns the SPA's index.html (HTML, status 200)
// for /api/* — that counts as NOT available.
let proxyAvailable = true;
fetch('/api/health')
  .then(async (r) => {
    const isJson = (r.headers.get('content-type') || '').includes('application/json');
    if (!r.ok || !isJson) {
      proxyAvailable = false;
      return;
    }
    const j = await r.json();
    proxyAvailable = !!j.openai;
  })
  .catch(() => {
    /* server unreachable — leave optimistic, errors will surface on use */
  });

export function hasOpenAIKey() {
  return proxyAvailable;
}

/* ---------- model choice + cost ----------
   Models the server allows (MODEL_PARAMS in server.js — keep in sync), with
   OpenAI's standard prices in USD per 1M tokens (checked 2026-09-29). */
export interface AIModel {
  id: string;
  label: string;
  inPrice: number;
  outPrice: number;
  retires?: string; // OpenAI shutdown date, shown next to the name
}
// Only models with no OpenAI shutdown date (checked 2026-10-20). Left out as
// retiring: every GPT-4 (gpt-4o, gpt-4o-mini, gpt-4.1, gpt-4.1-mini → gpt-6-luna;
// gpt-4.1-nano → gpt-5.6-luna), gpt-5 / gpt-5-mini / gpt-5-nano (→ gpt-5.6-sol /
// terra / luna) and gpt-5.4-nano (→ gpt-6-luna).
export const AI_MODELS: AIModel[] = [
  { id: 'gpt-6-luna', label: 'GPT-6 Luna', inPrice: 0.1, outPrice: 0.5 },
  { id: 'gpt-5.6-luna', label: 'GPT-5.6 Luna', inPrice: 0.2, outPrice: 1.2 },
  { id: 'gpt-5.4-mini', label: 'GPT-5.4 mini', inPrice: 0.75, outPrice: 4.5 },
  { id: 'gpt-6-sol', label: 'GPT-6 Sol', inPrice: 2, outPrice: 10 },
  { id: 'gpt-5.6-terra', label: 'GPT-5.6 Terra', inPrice: 2, outPrice: 12 },
  { id: 'gpt-5.6-sol', label: 'GPT-5.6 Sol', inPrice: 4, outPrice: 20 },
];
// OpenAI's named replacement for gpt-4o-mini, and cheaper than it was.
export const DEFAULT_AI_MODEL = 'gpt-6-luna';

// The model every generateField call uses; the panel sets it from the saved
// import defaults.
let currentModel = DEFAULT_AI_MODEL;
export function setAIModel(id: string) {
  currentModel = AI_MODELS.some((m) => m.id === id) ? id : DEFAULT_AI_MODEL;
}

const modelInfo = (id: string) => AI_MODELS.find((m) => m.id === id);
const usd = (m: AIModel, tin: number, tout: number) => (tin * m.inPrice + tout * m.outPrice) / 1e6;

// Estimated cost of "Generate ALL fields" for one location: the 6 always-on
// fields, tokens ≈ characters / 4 of the actual prompts plus ~60 tokens of
// location data, and typical answer lengths. A rough guide (a real run on
// a cheap model came out ~25% lower); the measured average is the true figure.
const TYPICAL_OUT: Record<string, number> = { description: 90 };
export function estimateCostPerLocation(id: string): number {
  const m = modelInfo(id);
  if (!m) return 0;
  let tin = 0;
  let tout = 0;
  for (const f of ['description', 'placeType', 'paymentStatus', 'services', 'activities', 'heightLimit']) {
    const sys = f === 'description' ? DESCRIPTION_SYSTEM_PROMPT : DATA_SYSTEM_PROMPT;
    tin += Math.round((sys.length + FIELD_PROMPTS[f].length + 120) / 4) + 60;
    tout += TYPICAL_OUT[f] ?? 15;
  }
  return usd(m, tin, tout);
}

// What the calls really cost, per model, from the token counts OpenAI
// reports. Kept in this browser so the average builds up over sessions.
export interface ModelUsage {
  calls: number;
  tin: number;
  tout: number;
  usd: number;
}
const USAGE_KEY = 'camplocation.aiUsage';
function readUsage(): Record<string, ModelUsage> {
  try {
    return JSON.parse(localStorage.getItem(USAGE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}
export function getAIUsage(): Record<string, ModelUsage> {
  return readUsage();
}
export function resetAIUsage() {
  try {
    localStorage.removeItem(USAGE_KEY);
  } catch {
    /* ignore */
  }
}
function recordUsage(id: string, tin: number, tout: number) {
  const m = modelInfo(id);
  if (!m || (!tin && !tout)) return;
  const all = readUsage();
  const u = all[id] || { calls: 0, tin: 0, tout: 0, usd: 0 };
  all[id] = { calls: u.calls + 1, tin: u.tin + tin, tout: u.tout + tout, usd: u.usd + usd(m, tin, tout) };
  try {
    localStorage.setItem(USAGE_KEY, JSON.stringify(all));
  } catch {
    /* ignore — the average just isn't kept */
  }
}

export interface LocationFacts {
  name?: string;
  type?: string;
  region?: string;
  services?: string[];
  activities?: string[];
  payment?: string;
  latitude?: number;
  longitude?: number;
}

// Allowed values for the app's enum fields (must match the in-app filter
// labels exactly, see the Filters screen). GPT may only choose from these.
export const SERVICES_ENUM = [
  'Toilets', 'Water', 'Shower', 'Kitchen/Dish wash', 'Laundrette', '4G internet', 'Access to Wifi',
  'Rubbish bins', 'Picnic table', 'BBQ/Campfire', 'Wastewater/Grey water', 'Black water', 'Electricity',
  'Electric car charger', 'Electric hook-up', 'Cafe/Restaurant', 'Shop/Groceries', 'Bottled gas', 'LPG',
  'Pets allowed', 'Vehicle wash',
];
export const ACTIVITIES_ENUM = [
  'Start of hikes', 'Swimming available', 'Fishing available', 'Kayak/boat release place',
  'Mountain bike tracks', 'City bike routes', 'Children playground', 'Point of view', 'Near touristic places',
];
// The app's "By Places" types (see Filters screen).
export const PLACETYPE_ENUM = [
  'Place in nature', 'Rest area', 'Motorway', 'Parking lot', 'Private parking lot', 'Campsite',
  'Caravan park', 'Free motorhome place', 'Paid motorhome place', 'Off road 4x4', 'Services',
];

// Contact info (phone/website/email) is only meaningful for run/staffed places
// like campsites and service points — not a bare layby or viewpoint. A place
// qualifies by place type OR by having one of these services.
export const CONTACT_INFO_PLACE_TYPES = ['Campsite', 'Caravan park', 'Free motorhome place', 'Paid motorhome place'];
export const CONTACT_INFO_SERVICES = ['Laundrette', 'Bottled gas', 'LPG', 'Vehicle wash'];
export function wantsContactInfo(placeType?: string, services?: string[]): boolean {
  if (placeType && CONTACT_INFO_PLACE_TYPES.includes(placeType)) return true;
  if (services && services.some((s) => CONTACT_INFO_SERVICES.includes(s))) return true;
  return false;
}

// Keep only values that exactly match the enum (case-insensitive), mapped back
// to their canonical spelling, deduped — so the output is always app-valid.
function filterToEnum(raw: string, allowed: string[]): string {
  const norm = (s: string) => s.trim().toLowerCase();
  const valid = raw
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => allowed.find((e) => norm(e) === norm(x)))
    .filter((x): x is string => !!x);
  return Array.from(new Set(valid)).join(', ');
}

// System prompts (the model's "role"). Exported so the UI can show them too.
export const DESCRIPTION_SYSTEM_PROMPT =
  'You write short, practical location notes for a camping / motorhome app, in the plain, matter-of-fact style campers use to tip each other off about a spot — facts and useful details only, no marketing language.';
export const DATA_SYSTEM_PROMPT =
  'You produce concise, factual data fields for outdoor and camping locations from your knowledge of real places.';

// Per-field instruction telling GPT exactly what to produce. Array fields
// (services / activities) must come back as a comma-separated list. These are
// the DEFAULTS — the admin panel can override any of them before generating.
export const FIELD_PROMPTS: Record<string, string> = {
  description:
    'a short, practical description written the way a camper or van-lifer would jot it down for others (see the style examples at the end). Use plain, factual, concise sentences — telegraphic notes are good (e.g. "Dogs welcome. Free Wifi. Water taps available."). Aim for 1–4 short sentences, about 60 words max. Where you know it — from the place type, the provided services/activities/payment, or genuine knowledge of this SPECIFIC named place — cover: what the place is (use its name if given), the pitch/parking type or setting, the key facilities, pricing or whether it is free/paid, and any access rules or restrictions, folding the services in naturally rather than naming the app fields. If you truly recognise this specific named place, use your real knowledge of it (prices, arrival times, pitch types). If you do NOT recognise it, keep it short and grounded only in its type, setting and the given facts — do NOT invent facilities, scenery, history or rules. On cost: if you GENUINELY recognise this specific named place and actually know its real prices, include the concrete figure (e.g. "From £20 per night.", "Pitches around €15."). Otherwise mention cost only as the given payment fact allows — "Free" or "Paid" (e.g. "Free to stay." / "Paid site.") — and when payment is "Unknown" or absent and you do not truly know a price, say NOTHING about cost. NEVER invent, guess or estimate a price, currency or amount for a place you do not actually know. You may note that a basic spot "has no services" when that is genuinely its nature (like a bare layby), otherwise mention only facilities that ARE present. NEVER write filler sentences stating that services, amenities, access rules, payment or details are missing, unavailable, "not detailed", "not specified", "not provided", "not mentioned" or "unknown" — simply leave out anything you do not know instead of pointing out that it is absent. Do NOT use marketing or flowery words like "nestled", "lush", "breathtaking", "serene", "gateway", "tranquil", or "tapestry". No emojis, and do NOT mention coordinates, latitude, or longitude. Examples of the desired STYLE only (do NOT copy their wording or place names): "Gwersylla Tymawr camping is a simple campsite with grass pitches. Campfires allowed. Eco toilets and hot showers. Prices from £7.50 per person per night." | "Very small layby for a small van with no services on a busy road." | "Public toilets at this location. Water fill station for bottles at the entrance."',
  placeType:
    'the single best-fitting place type. Use what you know about this specific named place, otherwise judge from its name and characteristics. Choose ONLY from this exact allowed set (use the exact spelling): ' +
    PLACETYPE_ENUM.join(', '),
  paymentStatus:
    'whether entry/use of this place is "Free" or "Paid". Use real knowledge of the named place when you have it; otherwise infer from the place type and region — for example developed or serviced campsites and caravan sites usually charge a fee (Paid), while public parking lots, viewpoints, picnic sites, rest areas and water points are usually Free. Reply "Unknown" only if you genuinely cannot tell. Reply with exactly one word: Free, Paid, or Unknown',
  activities:
    'the activities a visitor could reasonably do at or near this place, inferred from its type, setting and region (for example, campsites and picnic sites in nature commonly offer Start of hikes, Swimming available or Fishing available; lakeside or riverside spots often have Kayak/boat release place). Use real knowledge of the named place when you have it. Pick every option from this exact allowed set that plausibly applies (use the exact spelling); reply with an empty string only if none plausibly apply: ' +
    ACTIVITIES_ENUM.join(', '),
  services:
    'the services available at this place. Use real knowledge of the named place when you have it; otherwise include the services that are standard for this exact place type and setting — for example a developed campsite typically has Toilets, Water, Shower, Rubbish bins and Picnic table; a caravan site adds Electric hook-up and Wastewater/Grey water; a picnic site often has Picnic table, Rubbish bins and BBQ/Campfire; a basic or empty parking lot usually has none or only Rubbish bins. Do NOT invent unusual amenities. Pick every option from this exact allowed set that plausibly applies (use the exact spelling); reply with an empty string only if none plausibly apply: ' +
    SERVICES_ENUM.join(', '),
  heightLimit:
    'the vehicle height limit in metres if you know of one or one is clearly implied for this specific place, otherwise reply with an empty string',
  phone:
    'the contact phone number for this place, in the format it would normally be published (include the country code if you know it), but ONLY if you genuinely know it for this specific named place. Otherwise reply with an empty string. Never invent or guess a number.',
  website:
    'the official website URL for this place, but ONLY if you genuinely know it for this specific named place. Return the bare URL only (e.g. https://example.com), no label or surrounding text. Otherwise reply with an empty string. Never invent or guess a URL.',
  email:
    'the contact email address for this place, but ONLY if you genuinely know it for this specific named place. Otherwise reply with an empty string. Never invent or guess an address.',
};

// The fields the panel can generate, in display order — used to render the
// editable-prompt UI.
export const PROMPT_FIELDS = Object.keys(FIELD_PROMPTS);

// Generates one field for a location. Returns the raw string value (for array
// fields, a comma-separated list the caller should split). `instrOverride` lets
// the admin panel swap in an edited prompt for this field.
export async function generateField(
  field: string,
  facts: LocationFacts,
  instrOverride?: string
): Promise<string> {
  const instr = (instrOverride && instrOverride.trim()) || FIELD_PROMPTS[field] || 'a suitable value';
  // For the description, only drop coordinates (they tend to leak into the
  // text). Services/activities/payment ARE passed so the model can fold them
  // into the practical, camper-style write-up.
  const factsForPrompt =
    field === 'description'
      ? (({ latitude, longitude, ...rest }) => rest)(facts)
      : facts;
  const userPrompt =
    `For this location in a camping / outdoors app, provide ${instr}.\n` +
    'Return ONLY the value with no field name, labels, or surrounding quotes.\n' +
    'Location data:\n' +
    JSON.stringify(factsForPrompt);

  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not signed in');

  const res = await fetch('/api/generate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      model: currentModel,
      messages: [
        {
          role: 'system',
          content: field === 'description' ? DESCRIPTION_SYSTEM_PROMPT : DATA_SYSTEM_PROMPT,
        },
        { role: 'user', content: userPrompt },
      ],
      temperature: field === 'description' ? 0.35 : 0.2,
      // Descriptions get more room so a longer custom prompt isn't cut off.
      max_tokens: field === 'description' ? 250 : 120,
    }),
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`AI proxy ${res.status}: ${t.slice(0, 200)}`);
  }
  // A 200 with an HTML body means the host served the web page instead of the
  // API (typical nginx setup without an /api proxy) — fail with a clear cause.
  if (!(res.headers.get('content-type') || '').includes('application/json')) {
    throw new Error(
      '/api is not routed to server.js — the host returned the web page instead. ' +
        'On the VPS: run server.js and add an nginx "location /api/" proxy to it.'
    );
  }
  const j = await res.json();
  // An older server.js ignores `model` and reports no usage — nothing recorded then.
  if (j.model && j.usage) recordUsage(j.model, j.usage.in || 0, j.usage.out || 0);
  // Strip surrounding quotes and any stray array brackets (the model sometimes
  // answers an enum field with "[]" or "[a, b]" instead of a plain list).
  const out = (j.text || '').trim().replace(/^["'[\]]+|["'[\]]+$/g, '');

  // Force enum fields to valid app values regardless of what the model returned.
  if (field === 'services') return filterToEnum(out, SERVICES_ENUM);
  if (field === 'activities') return filterToEnum(out, ACTIVITIES_ENUM);
  return out;
}
